import {AfterViewInit, Component, OnInit, Renderer2, ViewChild} from '@angular/core';
import {MatPaginator} from "@angular/material/paginator";
import {MatSort} from "@angular/material/sort";
import {UtilsService} from "../shared/utils.service";
import {AppService} from "../app.service";
import {ApiRequestService} from "../shared/api-request.service";
import {MatDialog} from "@angular/material/dialog";
import {tap} from "rxjs/operators";
import {UserPublicProfileComponent} from "../shared/user-public-profile/user-public-profile.component";
import {ApiDataSource} from "../utils/api-data-source";
import {VendorModel} from "../models/vendor.model";
import {VendorsEditComponent} from "./vendors-edit/vendors-edit.component";
import {VendorsViewComponent} from "./vendors-view/vendors-view.component";
import {VendorsFilterComponent} from "./vendors-filter/vendors-filter.component";
import {saveAs} from "file-saver";
import moment from "moment";
import {AssetsService} from "../assets/assets.service";

@Component({
  selector: 'app-vendors',
  templateUrl: './vendors.component.html',
  styleUrls: ['./vendors.component.scss']
})
export class VendorsComponent implements OnInit, AfterViewInit {

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  dataSource = new VendorsDataSource(this.app, this.api);

  displayedColumns = [
    'select',
    'id',
    'name',
    'is_prequalified',
    'insurance_last_verified',
    'insurance_valid_till',
    'contact_details',
    'linked_assets_count',
    'created_by_user',
    'date_created',
    // 'date_created_UTC',
    'buttons'
  ];

  on_parent_list: boolean = true;

  constructor(
    public utils: UtilsService,
    public assetsService: AssetsService,
    public app: AppService,
    private api: ApiRequestService,
    private dialog: MatDialog,
    public renderer: Renderer2
  ) { }

  ngOnInit() {
    this.dataSource.getData(false);
  }

  ngAfterViewInit() {
    // Reset the paginator when sorting
    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));

    const _tap = tap(() => {
      this.dataSource.limit = this.paginator.pageSize;
      this.dataSource.offset = this.paginator.pageIndex;
      this.dataSource.order = this.sort.direction;

      // sorting for utc time by parsing original time
      if (this.sort.active === "date_created_UTC") {
        this.dataSource.order_by = "date_created";
      } else {
        this.dataSource.order_by = this.sort.active;
      }


      this.dataSource.getData(false);
    });

    // Subscribe to the paginator tap events
    this.paginator.page.pipe(_tap).subscribe();
    this.sort.sortChange.pipe(_tap).subscribe();
  }

  /**
   * Create a new Vendor.
   *
   * @return {void}
   */
  onAdd(): void {
    this.onEdit(new VendorModel());
  }

  /**
   * Create new or update existing Vendor.
   *
   * @param {VendorModel} vendor The vendor to be edited.
   * @return {void}
   */
  onEdit(vendor: VendorModel): void {
    this.dialog
      .open(VendorsEditComponent, {
        width: '1200px',
        data: {
          vendor_id: vendor.id
        }
      })
      .afterClosed()
      .subscribe(() => {
        // Refresh the list regardless of how the dialog is closed.
        this.dataSource.getData(true);
      });
  }

  /**
   * Displays Vendor data.
   *
   * @param {number} id - The unique identifier of the vendor.
   * @return {void}
   */
  onView(id: number): void {
    this.utils.showComponentDialog(
      VendorsViewComponent,
      id,
      { width: '700px' },
      () => {
        // Refresh the list regardless of how the dialog is closed.
        this.dataSource.getData(true);
      }
    );
  }

  /**
   * Archives selected Vendors.
   *
   * @return {void}
   */
  onRemoveSelected(): void {
    this.utils.showModal(
      'Archive Vendor(s)',
      `Are you sure you want to archive ${this.dataSource.selection.selected.length} vendor(s)?`,
      () => {
        this.remove(this.dataSource.selection.selected.join(','), 'Vendor(s) have been archived.');
      }
    );
  }

  /**
   * Archives the specified Vendor.
   *
   * @param {VendorModel} vendor - The vendor to be archived.
   * @return {void}
   */
  onRemove(vendor: VendorModel): void {
    this.utils.showModal(
      'Archive Vendor',
      `Are you sure you want to archive "${vendor.name}"?`,
      () => {
        this.remove(vendor.id, 'Vendor has been archived.');
      }
    );
  }

  /**
   * Archives Vendors with the specified IDs.
   *
   * @param {any} ids
   * @param msg
   * @return {void}
   */
  private remove(ids: any, msg: string): void {
    this.api.makeRequest('delete', `v2/vendors/${ids}`)
      .then((response) => {
        this.utils.showToast(msg);
        this.dataSource.selection.clear();
        this.dataSource.getData(false);
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  /**
   * Restore Vendor.
   *
   * @return {void}
   */
  onRestore(id: number): void {
    this.utils.showModal(
      'Restore Vendor',
      'Are you sure you want to restore this vendor?',
      () => {
        this.restore(id, 'The Vendor was restored.');
      }
    );
  }

  /**
   * Restore selected Vendors.
   *
   * @return {void}
   */
  onRestoreSelected(): void {
    this.utils.showModal(
      'Restore Selected Vendors',
      'Are you sure you want to restore the selected Vendors?',
      () => {
        this.restore(this.dataSource.selection.selected.join(','), 'The selected Vendors were restored.');
      }
    );
  }

  /**
   * Restores Vendors with the specified IDs.
   *
   * @param {any} ids
   * @param msg
   * @return {void}
   */
  private restore(ids: any, msg: string): void {
    this.api.makeRequest('put', `v2/vendors/${ids}/restore`)
      .then((response) => {
        this.utils.showToast(msg);
        this.dataSource.selection.clear();
        this.dataSource.getData();
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  /**
   * Show component dialog for filtering Vendors.
   *
   * @return {void}
   */
  onFilter(): void {
    this.utils.showComponentDialog(VendorsFilterComponent, {
        filter_is_prequalified: this.dataSource.is_prequalified,
        filter_archived: this.dataSource.archived,
        date_range: this.dataSource.insurance_expiry_date
      }, {
        width: '768px'
      },
      (results: any): void => {
        // Terminate early if it is undefined or an empty string.
        if ( typeof results === 'undefined' || results === '' ) {
          return;
        }

        // Apply the filters.
        this.dataSource.is_prequalified = results.is_prequalified ?? this.dataSource.is_prequalified;
        this.dataSource.archived = results.archived ?? this.dataSource.archived;
        this.dataSource.insurance_expiry_date = results.date_range ?? this.dataSource.insurance_expiry_date;
        this.dataSource.getData();
      }
    );
  }

  /**
   * Exports the list of Vendors into the specified format and sends a download request to the browser.
   *
   * @param type csv, xls, xlsx, pdf.
   */
  onExportSelected(type: string = 'csv'): void {
    this.dataSource.makeDownloadRequest(`v2/vendors/export/${type}` + (this.dataSource.selection.selected.length ? '/' + this.dataSource.selection.selected.join(',') : ''), {
      is_prequalified: this.dataSource.is_prequalified,
      archived: this.dataSource.archived,
      insurance_expiry_date_range: (this.dataSource.insurance_expiry_date && this.dataSource.insurance_expiry_date.length > 1) ? this.dataSource.insurance_expiry_date.map((date: Date) => {
        return date.getTime() / 1000;
      }).join(',') : ''
    })
      .then((response) => {
        saveAs(response, `Vendors - ${moment().toString()}.${type}`);
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  /**
   * Open the User's public profile dialog.
   *
   * @param hash
   * @return {void}
   */
  onUserPublicView(hash: string): void {
    this.utils.showComponentDialog(
      UserPublicProfileComponent,
      hash,
      { width: '90%' },
      () => {
        // Refresh the list regardless of how the dialog is closed.
        // this.dataSource.getData();
      }
    );
  }

}

export class VendorsDataSource extends ApiDataSource {
  order_by = 'id';
  order = 'asc';
  is_prequalified = "";
  archived = "false";
  insurance_expiry_date: Date[] = [];

  getData(resetOffset: boolean = false) {
    this.makeRequest('v2/vendors', resetOffset, {
      is_prequalified: this.is_prequalified,
      archived: this.archived,
      insurance_expiry_date_range: this.insurance_expiry_date.map((date: Date) => {
        return date.getTime() / 1000;
      }).join(',')
    });
  }
}
